import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * The Technology ROI Calculator — the fourth tool's own page.
 *
 * One file, per the convention PR #187 settled: the page and its route live
 * together, and `pagesFrom()` / `routesFrom()` discover both. The three older
 * tool pages are still stacked in `pages/tools.ts` with the hub, which is why
 * that file is the one two AI-editor runs collided on; this one is not in
 * anybody's path.
 *
 * The model, the industry table and the copy are in
 * `content/tools/roi-calculator.ts` — that file is where to argue with the
 * arithmetic. This one is only the frame around the widget.
 */
export const route: RouteDef = {
  id: 'toolsRoi',
  path: 'tools/roi-calculator',
  priority: 0.8,
}

export const toolsRoi: WrittenPage = {
  navLabel: 'Technology ROI Calculator',
  title: 'Technology ROI Calculator',
  description:
    'Your industry’s IT spend, your revenue and your objectives, against a model calibrated on real value cases. What your technology budget could return in a year: tangible, intangible and the cost of waiting.',
  sections: [
    {
      kind: 'hero',
      // Capitals spelled out rather than left to `text-transform`, for the
      // reason the ARI page records: CSS casing follows the document language
      // and Turkish maps `i` to `İ`, so this English name would come out
      // "TECHNOLOGY ROİ CALCULATOR" on /tr.
      eyebrow: 'TECHNOLOGY ROI CALCULATOR',
      headline: 'What is your technology budget actually worth?',
      sub: 'Most boards can say what technology costs and almost none can say what it returns. Four inputs (revenue, country, industry, and what you are trying to do) give you the number in three parts: what you can measure, what you can feel, and what standing still would have cost you.',
      ctas: [],
    },
    { kind: 'tool', tool: 'roi-calculator' },
    {
      kind: 'prose',
      check:
        'THE PRICE IS ON THE PAGE, BY DECISION. "Your Digitopia ROI" is the '
        + 'total annual value divided by our engagement price, printed beside '
        + 'the total — so a reader who divides one by the other has the price. '
        + 'Halil\'s source file comments those two constants "never displayed", '
        + 'which is no longer true. Raised with Kagan on 2026-08-29 before '
        + 'porting and answered "parayı görmelerinde sıkıntı yok, örnek '
        + 'hesaplama". Recorded here so it does not get quietly reversed as a '
        + 'leak.\n\n'
        + 'TURKISH IS WRITTEN as of 2026-08-30 — page, tool, twenty industry '
        + 'names, fifty country names and the seven objectives. The Guts Check '
        + 'and the ARI are still English, and still Halil\'s call before '
        + '1 September.',
      paragraphs: [],
    },
    {
      kind: 'prose',
      heading: 'Where the numbers come from',
      paragraphs: [
        'Your industry sets two things: what companies like yours spend on IT as a share of revenue, and how that budget splits between running what exists, growing it, and transforming it. Only the second and third parts create value, since Run simply keeps the lights on, so the model works off Grow-and-Transform, not off the whole budget. Your country adjusts the spend to its region, and your objectives adjust the multiple, because a cost-reduction programme and a new-revenue programme do not return the same thing per dollar.',
        'The multiple itself, and the ratios that turn tangible value into intangible value and avoided risk, are calibrated on a real case: a $1B discrete manufacturer with a $25M IT budget, 45% of it Grow-and-Transform, which came out at a 4.18x complete value ratio. That is one case, honestly stated. Treat the output as a range you can argue with, not a business case. The disclaimer under the report says the same thing.',
      ],
    },
    {
      kind: 'prose',
      heading: 'Why the intangible and the counterfactual are counted at all',
      paragraphs: [
        'Because leaving them out is how technology investment gets underwritten. A wrong strategic decision taken on bad data, a leadership team spending against each other, a quarter lost to a slipped programme: none of those appear in a benefit case, and all of them are paid for. The same is true of waiting: data debt, the people who leave, the competitor who does not wait. This model prices both, at ratios drawn from the same case rather than from optimism.',
        'What it will not do is tell you which of your programmes to fund. That needs your actual portfolio, and a baseline you can defend, which is what DAIMI measures and the natural next conversation from any number this produces.',
      ],
    },
  ],
}
